import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, ArrowLeft, TrendingUp, Clock, RefreshCw, Star, ShoppingBag, Receipt, Sparkles, Award, Zap, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../App';

const API = '/api';

const UserRewards = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state - default to 'balance', can be 'store' or 'redemptions'
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'balance');
  
  // Balance & History state
  const [balance, setBalance] = useState({ balance: 0, lifetime_earned: 0 });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 15;
  
  // Store state
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [redeemingItem, setRedeemingItem] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  
  // Redemptions state
  const [redemptions, setRedemptions] = useState([]);
  const [loadingRedemptions, setLoadingRedemptions] = useState(false);
  const [redemptionsTotal, setRedemptionsTotal] = useState(0);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/rewards/me/balance`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, []);

  const fetchEntries = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const response = await axios.get(`${API}/api/rewards/me`, {
        params: { page: pageNum, limit },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (append) {
        setEntries(prev => [...prev, ...(response.data.entries || [])]);
      } else {
        setEntries(response.data.entries || []);
      }
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error('Failed to load rewards');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const fetchCatalog = useCallback(async () => {
    try {
      setLoadingCatalog(true);
      const response = await axios.get(`${API}/api/rewards/catalog`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCatalog(response.data.catalog || []);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      toast.error('Failed to load store');
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  const fetchRedemptions = useCallback(async () => {
    try {
      setLoadingRedemptions(true);
      const response = await axios.get(`${API}/api/rewards/me/redemptions`, {
        params: { limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRedemptions(response.data.redemptions || []);
      setRedemptionsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    } finally {
      setLoadingRedemptions(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    fetchBalance();
    fetchEntries(1);
    fetchCatalog();
    fetchRedemptions();
  }, [user, authLoading, navigate, fetchBalance, fetchEntries, fetchCatalog, fetchRedemptions]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchEntries(nextPage, true);
  };

  const handleRefresh = async () => {
    setPage(1);
    await Promise.all([fetchBalance(), fetchEntries(1), fetchCatalog(), fetchRedemptions()]);
    toast.success('Refreshed');
  };

  const handleRedeem = async (item) => {
    setRedeemingItem(item.item_key);
    try {
      const response = await axios.post(`${API}/api/rewards/redeem`, 
        { item_key: item.item_key },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success(response.data.message || 'Redeemed successfully!');
      setConfirmModal(null);
      
      // Refresh data
      await Promise.all([fetchBalance(), fetchEntries(1), fetchRedemptions()]);
      
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to redeem';
      toast.error(msg);
    } finally {
      setRedeemingItem(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getReasonIcon = (reasonCode) => {
    switch (reasonCode) {
      case 'bug_report': return '🐛';
      case 'early_supporter': return '⭐';
      case 'featured_contributor': return '🏆';
      case 'community_help': return '🤝';
      case 'manual_grant': return '🎁';
      case 'redeem': return '🛒';
      default: return '💎';
    }
  };

  const getItemIcon = (itemKey) => {
    switch (itemKey) {
      case 'featured_7d': return <Sparkles className="w-6 h-6 text-purple-400" />;
      case 'early_creator_badge': return <Award className="w-6 h-6 text-yellow-400" />;
      case 'profile_boost_24h': return <Zap className="w-6 h-6 text-blue-400" />;
      default: return <Gift className="w-6 h-6 text-zinc-400" />;
    }
  };

  const hasMore = entries.length < total;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="user-rewards">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-yellow-500" />
                <h1 className="text-xl font-bold">Rewards</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 rounded-full">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-semibold">{balance.balance.toLocaleString()} pts</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                className="border-zinc-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4">
            <button
              onClick={() => handleTabChange('balance')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'balance'
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
              data-testid="tab-balance"
            >
              <TrendingUp className="w-4 h-4" />
              Balance
            </button>
            <button
              onClick={() => handleTabChange('store')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'store'
                  ? 'bg-purple-500/20 text-purple-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
              data-testid="tab-store"
            >
              <ShoppingBag className="w-4 h-4" />
              Store
            </button>
            <button
              onClick={() => handleTabChange('redemptions')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'redemptions'
                  ? 'bg-green-500/20 text-green-400'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
              data-testid="tab-redemptions"
            >
              <Receipt className="w-4 h-4" />
              Redemptions
              {redemptionsTotal > 0 && (
                <Badge variant="outline" className="ml-1 text-xs border-zinc-600">
                  {redemptionsTotal}
                </Badge>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Mobile Balance Display */}
        <div className="sm:hidden mb-4 flex justify-center">
          <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 rounded-full">
            <Star className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-bold text-lg">{balance.balance.toLocaleString()} pts</span>
          </div>
        </div>

        {/* BALANCE TAB */}
        {activeTab === 'balance' && (
          <>
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-200/70 mb-1">Current Balance</p>
                      <p className="text-4xl font-bold text-yellow-400" data-testid="current-balance">{balance.balance.toLocaleString()}</p>
                      <p className="text-sm text-yellow-200/50 mt-1">points</p>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Star className="w-8 h-8 text-yellow-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-zinc-400 mb-1">Lifetime Earned</p>
                      <p className="text-4xl font-bold text-white" data-testid="lifetime-earned">{balance.lifetime_earned.toLocaleString()}</p>
                      <p className="text-sm text-zinc-500 mt-1">total points</p>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                      <TrendingUp className="w-8 h-8 text-green-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* History Section */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-zinc-400" />
                  Reward History
                  {total > 0 && (
                    <Badge variant="outline" className="ml-2 border-zinc-700 text-zinc-400">
                      {total} entries
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {entries.length === 0 ? (
                  <div className="text-center py-12">
                    <Gift className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                    <h3 className="text-lg font-semibold text-zinc-400 mb-2">No rewards yet</h3>
                    <p className="text-zinc-500">Your reward history will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {entries.map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className="flex items-start gap-4 p-4 bg-zinc-800/50 rounded-xl"
                      >
                        <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-lg flex-shrink-0">
                          {getReasonIcon(entry.reason_code)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-white">{entry.reason_label}</p>
                            <span className={`font-bold whitespace-nowrap ${entry.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {entry.amount >= 0 ? '+' : ''}{entry.amount}
                            </span>
                          </div>
                          {entry.note && (
                            <p className="text-sm text-zinc-400 mt-1">{entry.note}</p>
                          )}
                          <p className="text-xs text-zinc-500 mt-2">{formatDate(entry.created_at)}</p>
                        </div>
                      </div>
                    ))}
                    
                    {hasMore && (
                      <div className="text-center pt-4">
                        <Button 
                          variant="outline" 
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          className="border-zinc-700"
                        >
                          {loadingMore ? 'Loading...' : 'Load More'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* STORE TAB */}
        {activeTab === 'store' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Rewards Store</h2>
              <p className="text-zinc-400">Redeem your points for exclusive perks</p>
            </div>

            {loadingCatalog ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent"></div>
              </div>
            ) : catalog.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                <h3 className="text-lg font-semibold text-zinc-400 mb-2">Store coming soon</h3>
                <p className="text-zinc-500">Check back later for redeemable items</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {catalog.map((item) => {
                  const canAfford = balance.balance >= item.cost;
                  const isRedeeming = redeemingItem === item.item_key;
                  
                  return (
                    <Card 
                      key={item.item_key} 
                      className={`border transition-all ${
                        canAfford 
                          ? 'bg-zinc-900 border-zinc-700 hover:border-purple-500/50' 
                          : 'bg-zinc-900/50 border-zinc-800 opacity-60'
                      }`}
                      data-testid={`store-item-${item.item_key}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            {getItemIcon(item.item_key)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h3 className="font-semibold text-white text-lg">{item.name}</h3>
                                <p className="text-zinc-400 text-sm mt-1">{item.description}</p>
                                {item.one_time && (
                                  <Badge variant="outline" className="mt-2 border-yellow-500/30 text-yellow-400 text-xs">
                                    One-time purchase
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="flex items-center gap-1 mb-2">
                                  <Star className="w-4 h-4 text-yellow-400" />
                                  <span className="font-bold text-yellow-400 text-xl">{item.cost}</span>
                                </div>
                                <Button
                                  onClick={() => setConfirmModal(item)}
                                  disabled={!canAfford || isRedeeming}
                                  className={`${
                                    canAfford 
                                      ? 'bg-purple-600 hover:bg-purple-700' 
                                      : 'bg-zinc-700 cursor-not-allowed'
                                  }`}
                                  data-testid={`redeem-btn-${item.item_key}`}
                                >
                                  {isRedeeming ? 'Redeeming...' : canAfford ? 'Redeem' : 'Not enough'}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* REDEMPTIONS TAB */}
        {activeTab === 'redemptions' && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5 text-zinc-400" />
                Redemption History
                {redemptionsTotal > 0 && (
                  <Badge variant="outline" className="ml-2 border-zinc-700 text-zinc-400">
                    {redemptionsTotal} total
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRedemptions ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent"></div>
                </div>
              ) : redemptions.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
                  <h3 className="text-lg font-semibold text-zinc-400 mb-2">No redemptions yet</h3>
                  <p className="text-zinc-500">Visit the store to redeem your points</p>
                  <Button 
                    onClick={() => handleTabChange('store')} 
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Go to Store
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {redemptions.map((redemption) => (
                    <div 
                      key={redemption.id}
                      className={`flex items-start gap-4 p-4 rounded-xl ${
                        redemption.status === 'success' 
                          ? 'bg-green-500/10 border border-green-500/20' 
                          : 'bg-red-500/10 border border-red-500/20'
                      }`}
                      data-testid={`redemption-${redemption.id}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        redemption.status === 'success' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {redemption.status === 'success' 
                          ? <CheckCircle className="w-5 h-5 text-green-400" />
                          : <XCircle className="w-5 h-5 text-red-400" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium text-white">{redemption.item_name}</p>
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400" />
                            <span className="text-yellow-400 font-semibold">-{redemption.cost}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={redemption.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                            {redemption.status}
                          </Badge>
                          {redemption.failure_reason && (
                            <span className="text-xs text-red-400">{redemption.failure_reason}</span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500 mt-2">{formatDate(redemption.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="redeem-confirm-modal">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                {getItemIcon(confirmModal.item_key)}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Confirm Redemption</h3>
                <p className="text-zinc-400 text-sm">{confirmModal.name}</p>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">Cost</span>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span className="font-bold text-yellow-400">{confirmModal.cost} points</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-zinc-400">Your balance</span>
                <span className="text-white">{balance.balance.toLocaleString()} points</span>
              </div>
              <div className="border-t border-zinc-700 mt-3 pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">After redemption</span>
                  <span className="text-green-400 font-semibold">{(balance.balance - confirmModal.cost).toLocaleString()} points</span>
                </div>
              </div>
            </div>

            <p className="text-zinc-400 text-sm mb-6">
              Are you sure you want to redeem <span className="text-white font-medium">{confirmModal.cost} points</span> for <span className="text-purple-400 font-medium">{confirmModal.name}</span>?
            </p>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 border-zinc-600"
                onClick={() => setConfirmModal(null)}
                disabled={redeemingItem === confirmModal.item_key}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={() => handleRedeem(confirmModal)}
                disabled={redeemingItem === confirmModal.item_key}
                data-testid="confirm-redeem-btn"
              >
                {redeemingItem === confirmModal.item_key ? 'Redeeming...' : 'Confirm Redeem'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRewards;
